import { Button, Drawer, Input, Segmented, Table } from 'antd';
import type { TableColumnsType, TableProps } from 'antd';
import { useEffect, useState, type ReactNode } from 'react';

import { ProjectSubnav } from './workspace-shell.tsx';

export type ModuleViewMode = 'split' | 'table';

export interface ModuleStat {
  label: string;
  value: string;
  note?: string;
  tone?: 'dark' | 'danger' | 'info';
}

export function ModulePageHeader({ projectId, kicker, title, copy, actions }: { projectId: string; kicker: string;
  title: string; copy: string; stats?: ModuleStat[]; actions?: ReactNode }) {
  return <><header className="module-page-head">
      <div className="module-head-main"><div className="module-kicker">{kicker}</div><h1>{title}</h1><p>{copy}</p></div>
      {actions && <div className="module-head-actions">{actions}</div>}
    </header>
    <div className="module-project-tabs"><ProjectSubnav projectId={projectId} /></div></>;
}

export function ModuleFullDetailDrawer({ open, onClose, children, width = 940 }: {
  open: boolean; onClose: () => void; children: ReactNode; width?: number;
}) {
  return <Drawer open={open} onClose={onClose} width={width} rootClassName="module-inspector-drawer"
    closable={false} styles={{ body: { padding: 0 } }}>
    <button type="button" className="module-drawer-close" onClick={onClose} aria-label="关闭详情">✕</button>
    <div className="module-inspector-drawer-body">{children}</div>
  </Drawer>;
}

export interface ModuleFilterOption {
  value: string;
  label: string;
  count?: number;
}

export function ModuleViewSwitch({ value, onChange }: { value: ModuleViewMode;
  onChange: (value: ModuleViewMode) => void }) {
  return <Segmented className="module-view-switch" value={value} aria-label="视图切换"
    onChange={(next) => onChange(next as ModuleViewMode)}
    options={[{ label: '分栏', value: 'split' }, { label: '表格', value: 'table' }]} />;
}

export function ModuleFilterTabs({ options, value, onChange }: { options: ModuleFilterOption[];
  value: string; onChange: (value: string) => void }) {
  return <div className="module-filter-tabs" role="tablist">{options.map((option) =>
    <button key={option.value} type="button" role="tab" aria-selected={value === option.value}
      className={value === option.value ? 'is-active' : ''} onClick={() => onChange(option.value)}>
      {option.label}{typeof option.count === 'number' && <em>{option.count}</em>}
    </button>)}</div>;
}

export function ModuleTablePanel({ title, count, filters, filterValue, onFilterChange, searchValue,
  onSearchChange, searchPlaceholder, viewMode, onViewChange, action, children }: {
  title: string; count: number;
  filters?: ModuleFilterOption[] | undefined; filterValue?: string | undefined;
  onFilterChange?: ((value: string) => void) | undefined;
  searchValue: string; onSearchChange: (value: string) => void; searchPlaceholder: string;
  viewMode: ModuleViewMode; onViewChange: (value: ModuleViewMode) => void;
  action?: ReactNode;
  children: ReactNode;
}) {
  return <section className="data-panel">
    <header className="data-panel-head">
      <div className="data-panel-title"><span>{title}</span><b>{count} 条</b></div>
      {action && <div className="data-panel-actions">{action}</div>}
      <div className="data-panel-tools">
        {filters && filterValue !== undefined && onFilterChange &&
          <ModuleFilterTabs options={filters} value={filterValue} onChange={onFilterChange} />}
        <Input.Search className="data-panel-search" allowClear value={searchValue}
          onChange={(event) => onSearchChange(event.target.value)} placeholder={searchPlaceholder} />
        <ModuleViewSwitch value={viewMode} onChange={onViewChange} />
      </div>
    </header>
    {children}
  </section>;
}

export function ModuleTableView<T extends object>({ rows, columns, rowKey, onOpen, focusedKey,
  loading = false, emptyText = '没有符合当前筛选的记录', minWidth = 1200, rowSelection }: {
  rows: T[]; columns: TableColumnsType<T>; rowKey: (row: T) => string;
  onOpen: (row: T) => void; focusedKey?: string | null | undefined; loading?: boolean;
  emptyText?: string; minWidth?: number; rowSelection?: TableProps<T>['rowSelection'];
}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const rowSignature = rows.map(rowKey).join('|');
  useEffect(() => { setPage(1); }, [rowSignature]);
  return <section className="module-table-view">
    <Table<T> className="module-table" rowKey={rowKey} size="small" columns={columns} dataSource={rows}
      loading={loading} {...(rowSelection ? { rowSelection } : {})} scroll={{ x: minWidth, y: 'calc(100vh - 390px)' }}
      sticky locale={{ emptyText }}
      pagination={{ current: page, pageSize, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'],
        showTotal: (total) => `共 ${total} 条`,
        onChange: (nextPage, nextSize) => { setPage(nextPage); setPageSize(nextSize); } }}
      rowClassName={(row) => rowKey(row) === focusedKey ? 'is-focused' : ''}
      onRow={(row) => ({ tabIndex: 0, onClick: () => onOpen(row),
        onKeyDown: (event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onOpen(row); } } })} />
  </section>;
}

export interface ModuleDetailFact {
  label: string;
  value: string;
}

export function ModuleDetailDrawer({ open, onClose, kicker, title, status, facts, note, primary,
  children }: {
  open: boolean; onClose: () => void; kicker: string; title: string; status?: ReactNode;
  facts: ModuleDetailFact[]; note?: string | undefined; primary?: ReactNode; children?: ReactNode;
}) {
  return <Drawer open={open} onClose={onClose} width={840} rootClassName="module-detail-drawer"
    styles={{ body: { padding: 0 } }}>
    <div className="module-detail">
      <header className="module-detail-hero">
        <button type="button" className="module-detail-close" onClick={onClose} aria-label="关闭详情">✕</button>
        <small>{kicker}</small><h2>{title}</h2>{status}
      </header>
      <div className="module-facts">{facts.map((fact) =>
        <div key={fact.label}><span>{fact.label}</span><strong>{fact.value}</strong></div>)}</div>
      {note && <p className="module-detail-note">{note}</p>}
      {children}
      <div className="module-detail-actions"><Button onClick={onClose}>关闭</Button>{primary}</div>
    </div>
  </Drawer>;
}
